import React from 'react';
import { AlertTriangle, RefreshCw, X, Info, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { Button } from './button';
import { cn } from '../../lib/utils';
import type { ApiError, ErrorInfo } from '../../utils/errorHandling';
import { getErrorInfo } from '../../utils/errorHandling';

export interface ErrorDisplayProps {
  error: ApiError | Error | string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  showDetails?: boolean;
  compact?: boolean;
}

/**
 * Reusable error display component with retry and dismiss actions
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  onDismiss,
  className,
  showDetails = false,
  compact = false,
}) => {
  const errorInfo: ErrorInfo = React.useMemo(() => {
    if (typeof error === 'string') {
      return {
        title: 'Error',
        message: error,
        severity: 'error',
      };
    }
    
    if (error instanceof Error) {
      const apiError = error as ApiError;
      return getErrorInfo(apiError);
    }
    
    return {
      title: 'Unknown Error',
      message: 'An unexpected error occurred',
      severity: 'error',
    };
  }, [error]);

  const getIcon = () => {
    switch (errorInfo.severity) {
      case 'warning':
        return <AlertTriangle className="h-4 w-4" />;
      case 'info':
        return <Info className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getVariant = () => {
    return errorInfo.severity === 'error' ? 'destructive' : 'default';
  };

  if (compact) {
    return (
      <div className={cn(
        'flex items-center space-x-2 text-sm',
        errorInfo.severity === 'error' ? 'text-destructive' : 'text-muted-foreground',
        className
      )}>
        {getIcon()}
        <span>{errorInfo.message}</span>
        {onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            className="h-6 px-2"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-6 px-2"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <Alert variant={getVariant()} className={className}>
      {getIcon()}
      <div className="flex-1">
        <AlertTitle>{errorInfo.title}</AlertTitle>
        <AlertDescription className="mt-1">
          {errorInfo.message}
          {showDetails && error instanceof Error && error.stack && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs opacity-70">
                Technical Details
              </summary>
              <pre className="mt-1 text-xs opacity-70 whitespace-pre-wrap">
                {error.stack}
              </pre>
            </details>
          )}
        </AlertDescription>
      </div>
      
      <div className="flex items-center space-x-2 ml-4">
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="h-8"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            {errorInfo.action || 'Retry'}
          </Button>
        )}
        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-8 w-8 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </Alert>
  );
};

/**
 * Inline error message component for form fields
 */
export interface InlineErrorProps {
  error?: string | null;
  className?: string;
}

export const InlineError: React.FC<InlineErrorProps> = ({ error, className }) => {
  if (!error) return null;
  
  return (
    <div className={cn('flex items-center space-x-1 text-sm text-destructive mt-1', className)}>
      <AlertCircle className="h-3 w-3" />
      <span>{error}</span>
    </div>
  );
};

/**
 * Error boundary fallback component
 */
export interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <AlertCircle className="h-12 w-12 text-destructive mb-4" />
      <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
      <p className="text-muted-foreground mb-4 max-w-md">
        An unexpected error occurred while rendering this component. 
        This has been logged and our team will investigate.
      </p>
      
      <div className="flex space-x-2">
        <Button onClick={resetError} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
        <Button 
          onClick={() => window.location.reload()} 
          variant="default"
        >
          Reload Page
        </Button>
      </div>
      
      <details className="mt-6 text-left">
        <summary className="cursor-pointer text-sm text-muted-foreground">
          Technical Details
        </summary>
        <pre className="mt-2 text-xs text-muted-foreground bg-muted p-3 rounded max-w-2xl overflow-auto">
          {error.message}
          {'\n\n'}
          {error.stack}
        </pre>
      </details>
    </div>
  );
};